/**
 * EditarViewPreInvoiceModal - Modal para editar prefactura
 *
 * Permite editar la información general y logística de una prefactura
 * con dos tabs: General y Logística
 */
import { Button } from '@shared/components/ui/button';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter/types';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faLocationDot, faCheck } from '@fortawesome/pro-light-svg-icons';
import { MovimientosIcon } from '@shared/components/ui/icons/warehouse.icons';

export interface EditarViewPreInvoiceModalRef {
  open: (data: EditarViewPreInvoiceModalData) => void;
  close: () => void;
}

export interface EditarViewPreInvoiceModalData {
  // Información general
  reference: string;
  shop: string;
  client: string;
  paymentMethod: string;
  internalId: string;
  shippingPrice: string;
  preparationPrice: string;
  codPrice: string;
  totalPrice: string;

  // Información logística
  shippingOrReturn: string;
  courier: string;
  orderWeight: string;
  numberOfLines: string;

  // Origen
  originCountry: string;
  originMunicipality: string;
  originPostalCode: string;

  // Destino
  destinationCountry: string;
  destinationMunicipality: string;
  destinationPostalCode: string;

  onSave: (data: EditarViewPreInvoiceModalData) => void;
}

export const EditarViewPreInvoiceModal = forwardRef<EditarViewPreInvoiceModalRef>((_, ref) => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { t: tCommon } = useTranslation('common');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const currency = useCurrency();

  // Estado del modal
  const [modalData, setModalData] = useState<EditarViewPreInvoiceModalData>({
    reference: '',
    shop: '',
    client: '',
    paymentMethod: '',
    internalId: '',
    shippingPrice: '',
    preparationPrice: '',
    codPrice: '',
    totalPrice: '',
    shippingOrReturn: '',
    courier: '',
    orderWeight: '',
    numberOfLines: '',
    originCountry: '',
    originMunicipality: '',
    originPostalCode: '',
    destinationCountry: '',
    destinationMunicipality: '',
    destinationPostalCode: '',
    onSave: () => {},
  });

  // 🎯 Guardar datos originales para detectar cambios reales
  const [originalData, setOriginalData] = useState<EditarViewPreInvoiceModalData | null>(null);

  // 🎯 Estado para controlar qué campo está siendo editado
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // 🎯 Estado para trackear si hay cambios sin guardar
  const [hasChanges, setHasChanges] = useState(false);
  const [attemptingToClose, setAttemptingToClose] = useState(false);

  // 🎯 Manejar el intento de cerrar la modal
  const handleCloseAttempt = useCallback(() => {
    if (hasChanges && !attemptingToClose) {
      setAttemptingToClose(true);
      confirmModalRef.current?.open(
        () => {
          setHasChanges(false);
          setAttemptingToClose(false);
          modalRef.current?.closeModal();
        },
        () => {
          setAttemptingToClose(false);
          setTimeout(() => {
            if (!modalRef.current) return;
            modalRef.current.openModal();
          }, 0);
        },
        t('modals.editar_prefactura.unsaved_changes.title', 'Cambios sin guardar'),
        t(
          'modals.editar_prefactura.unsaved_changes.message',
          '¿Estás seguro de que deseas cerrar? Los cambios realizados se perderán.',
        ),
        tCommon('modal.close', 'Cerrar sin guardar'),
        tCommon('modal.cancel', 'Cancelar'),
      );
    } else if (!hasChanges) {
      setAttemptingToClose(false);
      modalRef.current?.closeModal();
    }
  }, [hasChanges, attemptingToClose, t, tCommon]);

  useImperativeHandle(
    ref,
    () => ({
      open: (data: EditarViewPreInvoiceModalData) => {
        setModalData(data);
        setOriginalData(data);
        setEditingField(null);
        setEditValue('');
        setHasChanges(false);
        setAttemptingToClose(false);
        modalRef.current?.openModal();
      },
      close: () => {
        handleCloseAttempt();
      },
    }),
    [handleCloseAttempt],
  );

  // 🎯 Función helper para verificar si hay cambios
  const checkForChanges = (currentData: EditarViewPreInvoiceModalData) => {
    if (!originalData) {
      setHasChanges(false);
      return;
    }

    const editableFields: (keyof EditarViewPreInvoiceModalData)[] = [
      'shippingPrice',
      'preparationPrice',
      'codPrice',
      'shippingOrReturn',
      'courier',
      'orderWeight',
      'numberOfLines',
    ];

    const anyChange = editableFields.some((field) => currentData[field] !== originalData[field]);

    setHasChanges(anyChange);
  };

  const handleSave = () => {
    modalData.onSave(modalData);
    setHasChanges(false);
    modalRef.current?.closeModal();
  };

  // 🎯 Función para extraer solo el número de un valor con moneda
  const extractNumber = (value: string): string => {
    return value.replace(/[^\d,.-]/g, '');
  };

  // 🎯 Función para iniciar la edición de un campo
  const handleStartEdit = (fieldName: string, currentValue: string) => {
    setEditingField(fieldName);
    const priceFields = ['shippingPrice', 'preparationPrice', 'codPrice'];
    if (priceFields.includes(fieldName)) {
      setEditValue(extractNumber(currentValue));
    } else {
      setEditValue(currentValue);
    }
  };

  // 🎯 Función para guardar el valor editado
  const handleSaveField = (fieldName: keyof EditarViewPreInvoiceModalData) => {
    const priceFields = ['shippingPrice', 'preparationPrice', 'codPrice'];
    let finalValue = editValue;

    if (priceFields.includes(fieldName)) {
      const numericValue = parseFloat(editValue.replace(',', '.')) || 0;
      finalValue =
        new Intl.NumberFormat('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numericValue) +
        ' ' +
        currency;
    }

    const originalValue = originalData?.[fieldName] || '';
    const valueChanged = finalValue !== originalValue;

    setModalData((prev) => ({
      ...prev,
      [fieldName]: finalValue,
    }));
    setEditingField(null);
    setEditValue('');

    if (valueChanged) {
      setHasChanges(true);
    } else {
      checkForChanges({ ...modalData, [fieldName]: finalValue });
    }
  };

  // 🎯 Recalcular precio total
  useEffect(() => {
    const parsePrice = (priceString: string): number => {
      const cleanedPrice = priceString.replace(/[^\d,.-]/g, '').replace(',', '.');
      return parseFloat(cleanedPrice) || 0;
    };

    const shipping = parsePrice(modalData.shippingPrice);
    const preparation = parsePrice(modalData.preparationPrice);
    const cod = parsePrice(modalData.codPrice);
    const total = shipping + preparation + cod;

    const formattedTotal =
      new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(total) +
      ' ' +
      currency;

    setModalData((prev) => {
      if (prev.totalPrice !== formattedTotal) {
        return {
          ...prev,
          totalPrice: formattedTotal,
        };
      }
      return prev;
    });
  }, [modalData.shippingPrice, modalData.preparationPrice, modalData.codPrice, currency]);

  // 🎯 Función para cancelar la edición
  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  // 🎯 Componente reutilizable para campos editables
  const EditableField = ({
    fieldName,
    value,
    label,
    testId,
  }: {
    fieldName: keyof EditarViewPreInvoiceModalData;
    value: string;
    label: string;
    testId: string;
  }) => {
    const isEditing = editingField === fieldName;

    return (
      <div className="flex justify-between items-center">
        <span className="text-sm text-complementary-text">{label}</span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveField(fieldName);
                } else if (e.key === 'Escape') {
                  handleCancelEdit();
                }
              }}
              onBlur={() => handleSaveField(fieldName)}
              className="h-7 w-32 text-sm"
              autoFocus
              data-testid={`${testId}-input`}
            />
            <FontAwesomeIcon
              icon={faCheck}
              className="text-success hover:text-bright-success cursor-pointer text-xs"
              onClick={() => handleSaveField(fieldName)}
              data-testid={`${testId}-save`}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-text font-medium">{value}</span>
            <FontAwesomeIcon
              icon={faPencil}
              className="text-icons hover:text-primary cursor-pointer text-xs"
              onClick={() => handleStartEdit(fieldName, value)}
              data-testid={testId}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('modals.editar_prefactura.title', 'Editar prefactura')}
        type="default"
        width="lg"
        onCloseModal={handleCloseAttempt}
        data-testid="editar-view-preinvoice-modal"
      >
        <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
          <Tabs defaultValue="general" className="w-full">
            {/* Encabezado con tabs */}
            <TabsList className="block ml-auto w-fit mb-[-40px]">
              <TabsTrigger value="general" data-testid="tab-general" className="text-xs py-2">
                {t('modals.editar_prefactura.tabs.general', 'General')}
              </TabsTrigger>
              <TabsTrigger value="logistica" data-testid="tab-logistica" className="text-xs py-2">
                {t('modals.editar_prefactura.tabs.logistica', 'Logística')}
              </TabsTrigger>
            </TabsList>

            {/* Contenido Tab General */}
            <TabsContent value="general" className="space-y-4">
              <h3 className="font-medium text-title mb-6 mt-2">
                {t('modals.editar_prefactura.general.title', 'Información general')}
              </h3>

              {/* Grid de dos columnas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bloque izquierdo */}
                <SectionBox className="bg-card-bg">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.general.reference', 'Referencia')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.reference}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.general.shop', 'Tienda')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.shop}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.general.client', 'Cliente')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.client}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.general.payment_method', 'Método de pago')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.paymentMethod}</span>
                    </div>
                  </div>
                </SectionBox>

                {/* Bloque derecho - Precios */}
                <SectionBox className="bg-card-bg">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.general.internal_id', 'ID Interno')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.internalId}</span>
                    </div>

                    <EditableField
                      fieldName="shippingPrice"
                      value={modalData.shippingPrice}
                      label={t('modals.editar_prefactura.general.shipping_price', 'Precio envío')}
                      testId="edit-shipping-price"
                    />

                    <EditableField
                      fieldName="preparationPrice"
                      value={modalData.preparationPrice}
                      label={t('modals.editar_prefactura.general.preparation_price', 'Precio preparación')}
                      testId="edit-preparation-price"
                    />

                    <EditableField
                      fieldName="codPrice"
                      value={modalData.codPrice}
                      label={t('modals.editar_prefactura.general.cod_price', 'Precio COD')}
                      testId="edit-cod-price"
                    />
                  </div>
                </SectionBox>
              </div>

              {/* Bloque de precio total */}
              <SectionBox className="bg-card-bg sm:max-w-fit sm:ml-auto">
                <div className="flex justify-between items-center gap-6">
                  <span className="text-sm text-text">
                    {t('modals.editar_prefactura.general.total_price', 'Precio total')}:
                  </span>
                  <span className="text-base text-success font-semibold">{modalData.totalPrice}</span>
                </div>
              </SectionBox>

              {/* Botón guardar */}
              <div className="flex justify-end mt-6">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasChanges}
                  data-testid="editar-view-preinvoice-save-general"
                >
                  {t('modals.editar_prefactura.button', 'Guardar')}
                </Button>
              </div>
            </TabsContent>

            {/* Contenido Tab Logística */}
            <TabsContent value="logistica" className="space-y-4">
              <h3 className="font-medium text-title mb-6 mt-2">
                {t('modals.editar_prefactura.logistica.title', 'Detalles logísticos')}
              </h3>
              <SectionBox className="bg-card-bg">
                <div className="space-y-3">
                  <EditableField
                    fieldName="shippingOrReturn"
                    value={modalData.shippingOrReturn}
                    label={t('modals.editar_prefactura.logistica.shipping_or_return', 'Envio o devolución')}
                    testId="edit-shipping-or-return"
                  />

                  <EditableField
                    fieldName="courier"
                    value={modalData.courier}
                    label={t('modals.editar_prefactura.logistica.courier', 'Courier')}
                    testId="edit-courier"
                  />

                  <EditableField
                    fieldName="orderWeight"
                    value={modalData.orderWeight}
                    label={t('modals.editar_prefactura.logistica.order_weight', 'Peso del pedido')}
                    testId="edit-order-weight"
                  />

                  <EditableField
                    fieldName="numberOfLines"
                    value={modalData.numberOfLines}
                    label={t('modals.editar_prefactura.logistica.number_of_lines', 'N° de líneas')}
                    testId="edit-number-of-lines"
                  />
                </div>
              </SectionBox>

              {/* Grid de dos columnas para origen y destino */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bloque Origen */}
                <SectionBox className="bg-card-bg">
                  <div className="flex items-center gap-2 mb-4">
                    <MovimientosIcon className="text-icons w-4 h-4" />
                    <h4 className="font-medium text-title">
                      {t('modals.editar_prefactura.logistica.origin', 'Origen')}
                    </h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.logistica.country', 'País')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.originCountry}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.logistica.municipality', 'Municipio')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.originMunicipality}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.logistica.postal_code', 'Cód. Postal')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.originPostalCode}</span>
                    </div>
                  </div>
                </SectionBox>

                {/* Bloque Destino */}
                <SectionBox className="bg-card-bg">
                  <div className="flex items-center gap-2 mb-4">
                    <FontAwesomeIcon icon={faLocationDot} className="text-icons text-base" />
                    <h4 className="font-medium text-title">
                      {t('modals.editar_prefactura.logistica.destination', 'Destino')}
                    </h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.logistica.country', 'País')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.destinationCountry}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.logistica.municipality', 'Municipio')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.destinationMunicipality}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-complementary-text">
                        {t('modals.editar_prefactura.logistica.postal_code', 'Cód. Postal')}
                      </span>
                      <span className="text-sm text-text font-medium">{modalData.destinationPostalCode}</span>
                    </div>
                  </div>
                </SectionBox>
              </div>

              {/* Botón guardar */}
              <div className="flex justify-end mt-6">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasChanges}
                  data-testid="editar-view-preinvoice-save-logistica"
                >
                  {t('modals.editar_prefactura.button', 'Guardar')}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </ModalCenter>

      {/* Modal de confirmación */}
      <ConfirmModal ref={confirmModalRef} />
    </>
  );
});

EditarViewPreInvoiceModal.displayName = 'EditarViewPreInvoiceModal';
